import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import 'ol/ol.css'
import Feature from 'ol/Feature'
import Map from 'ol/Map'
import Point from 'ol/geom/Point'
import View from 'ol/View'
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style'
import { Cluster, OSM, Vector as VectorSource } from 'ol/source'
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer'
import withConfig from '../../wrappers/withConfig'

import ImageLayer from 'ol/layer/Image'
import ImageArcGISRest from 'ol/source/ImageArcGISRest'
import LayerSwitcher from 'ol-layerswitcher'
import 'ol-layerswitcher/dist/ol-layerswitcher.css'
import { FullScreen, defaults as defaultControls } from 'ol/control'
import Overlay from 'ol/Overlay'
import {boundingExtent} from 'ol/extent';
import { format } from "date-fns";
import { constructDetailUrl } from '../../../utils/constants/urls';



const setViewAroundExtent = (map, extent) => {
    map.getView().fit(extent)
    return null
}

const ClusterMap = withConfig(({ mapResults, initialBoundingBox, onReset, defaultBoundingBox, handleMapChange, lat, lon}) => {
    const mapRef = useRef(null)
    let [searchParams, setSearchParams] = useSearchParams()

    const [map, setMap] = useState(null) // map is initialized only once, on component render - which corresponds to useEffect(fn, []) (aka no dependencies)
    const [clusterLayer, setClusterLayer] = useState(null) // initialize clusterLayer as its own state variable for update (aka reacts to update on lat / lon)

    const initialExtent = useMemo(() => {
        if (initialBoundingBox) {
            // const { lowerLat, lowerLon, upperLat, upperLon } = initialBoundingBox
            const { north, south, east, west } = initialBoundingBox
            return [west, south, east, north]
        }
        return []
    }, [initialBoundingBox])
    
    const [ currentExtent, setCurrentExtent ] = useState(initialExtent)

    const mapReset = useCallback(() => {
        const { north, south, east, west } = defaultBoundingBox
        const defaultExtent = [west, south, east, north]
        setViewAroundExtent(map, defaultExtent)
        if (defaultExtent.some(x => !isNaN(x))) {
            setSearchParams({bbox: defaultExtent.join(',')}, { replace: false })
        }
        onReset()
    }, [map, 
        defaultBoundingBox,
        onReset])

    useEffect(() => {
        if (map) {
            setViewAroundExtent(map, initialExtent)
        }
    }, [map, initialExtent])

    useEffect(() => {
        const map = new Map({
            target: mapRef.current,
            controls: defaultControls().extend([new FullScreen()]),
            layers: [
                new TileLayer({
                    title: 'Map',
                    source: new OSM(),
                    visible: true,
                    type: 'base',
                }),
                new ImageLayer({
                    title: 'Satellite',
                    source: new ImageArcGISRest({
                        ratio: 1,
                        url: 'http://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer',
                    }),
                    visible: false,
                    opacity: 1,
                    type: 'base',
                }),
            ],
            view: new View({
                projection: 'EPSG:4326',
                center: [lon, lat],
                zoom: 7,
            }),
        })

        const layerSwitcher = new LayerSwitcher({
            reverse: false,
            groupSelectStyle: 'group',
            renderPanel: true,
        })

        map.addControl(layerSwitcher)

        map.on('moveend', (e) => {
            const newExtent = e.map.getView().calculateExtent()
            setCurrentExtent(newExtent)
            if (newExtent.some(x => !isNaN(x))) {
                setSearchParams({bbox: newExtent.join(',')}, { replace: true })
            }
        })
        
        //tooltip
        const tooltip = document.getElementById('tooltip');
        const overlay = new Overlay({
        element: tooltip,
        offset: [10, 0],
        positioning: 'bottom-left'
        });
        map.addOverlay(overlay);

        //hover tooltip
        function displayTooltip(evt) {
            const pixel = evt.pixel;
            
            const feature = map.forEachFeatureAtPixel(pixel, function(feature) {
            return feature;
        });
        const displayToolTip = feature && (feature.get('features').length === 1)
        tooltip.style.display = displayToolTip ? '' : 'none';
            if (displayToolTip) {
            overlay.setPosition(evt.coordinate);
                tooltip.innerHTML = 
                `<div>${feature.get('features')[0].get('name')}</div>`
            }
        };
        map.on('pointermove', displayTooltip);

        //clicked tooltip
        function displayFullTooltip(evt) {
            map.un('pointermove', displayTooltip);
            const pixel = evt.pixel;
            const feature = map.forEachFeatureAtPixel(pixel, function(feature) {
            return feature;
        });
        const displayFullTooltip = feature && (feature.get('features').length === 1)
        tooltip.style.display = displayFullTooltip ? '' : 'none';
            if (displayFullTooltip) {
                const job_end_date = `${feature.get('features')[0].get('job_end')}`
                overlay.setPosition(evt.coordinate);
                tooltip.innerHTML = 
                `<div>${feature.get('features')[0].get('name')}<br />${format(new Date(job_end_date), 'MM-dd-yyyy')}<br /><a href=./${feature.get('features')[0].get('link')}>View More</a></div>`
            }
            else{
                map.on('pointermove', displayTooltip);
            }
        };
        map.on('click', displayFullTooltip);

        setMap(map)
    }, [])

    useEffect(() => {
        // trigger refresh of list + map features
        const [west, south, east, north] = currentExtent
    
        handleMapChange(north, south, east, west)
    }, [currentExtent])

    const updateClusterLayers = useCallback(() => {
       const features = mapResults.map( result => {
        //    const transformedCoords = transform([coordinates], 'EPSG:3857', 'EPSG:4326');
            const coordinates = [result.Longitude, result.Latitude]
            // const date = format(new Date(), 'MM-dd-yyyy');

            const feature = new Feature({
                geometry: new Point(coordinates),
                job_end: result.JobEndDate,
                name: result.WellName,
                link: result.APINumber
                })
           return feature
       })

        const source = new VectorSource({
            features: features,
        })

        const clusterSource = new Cluster({
            distance: 10,
            minDistance: 10,
            source: source,
        })
        const styleCache = {}
        const clusters = new VectorLayer({
            source: clusterSource,
            style: function (feature) {
                const size = feature.get('features').length
                let style = styleCache[size]
                if (!style) {
                    style = new Style({
                        image: new CircleStyle({
                            radius: 10,
                            stroke: new Stroke({
                                color: '#fff',
                            }),
                            fill: new Fill({
                                color: '#3399CC',
                            }),
                        }),
                        text: new Text({
                            text: size.toString(),
                            fill: new Fill({
                                color: '#fff',
                            }),
                        }),
                    })
                    styleCache[size] = style
                }
                return style
            },
        })
        // remove the old cluster layer from the map, if it exists
        if (clusterLayer) {
            map.removeLayer(clusterLayer) 
        }
        setClusterLayer(clusters) // set the cluster layer state to the new cluster layer data
        map.addLayer(clusters) // add the new cluster layer to the map

        map.on('click', (e) => {
            clusters.getFeatures(e.pixel).then((clickedFeatures) => {
              if (clickedFeatures.length) {
                // Get clustered Coordinates
                const features = clickedFeatures[0].get('features');
                if (features.length > 1) {
                  const extent = boundingExtent(
                    features.map((r) => r.getGeometry().getCoordinates())
                  );
                  map.getView().fit(extent, {duration: 1000, padding: [50, 50, 50, 50]});
                }
              }
            });
          });

    }, [map, clusterLayer, mapResults])
    
    useEffect(() => {
        if (map && mapResults && mapResults.length) {
            updateClusterLayers()
        }
    }, [mapResults, map])


    
    return (
        <div>
            <div id="map" ref={mapRef} className="map"></div>
            <button type="button" className="map_reset" onClick={mapReset}>
                RESET
            </button>
            <div id="tooltip"></div>
        </div>
    )
})

export default ClusterMap
